import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { X, Package, AlertCircle, RefreshCw, Ban, Check, XCircle } from 'lucide-react';

const ErpDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [erpData, setErpData] = useState<{
    ticimaxProductId: number;
    ticimaxProductIds: Record<string, number>;
  }>({
    ticimaxProductId: 0,
    ticimaxProductIds: {}
  });
  const [closureRequests, setClosureRequests] = useState<Product[]>([]);
  const [closedProducts, setClosedProducts] = useState<Product[]>([]);
  const [showClosedProducts, setShowClosedProducts] = useState(false);
  const [reopenModal, setReopenModal] = useState<{ show: boolean; product: Product | null }>({ show: false, product: null });
  const [reopenNote, setReopenNote] = useState('');
  const [closureResponseModal, setClosureResponseModal] = useState<{
    show: boolean;
    product: Product | null;
    decision: 'approved' | 'rejected' | null;
  }>({ show: false, product: null, decision: null });
  const [responseNote, setResponseNote] = useState('');
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });

  useEffect(() => {
    loadProducts();
    loadClosureRequests();
    loadClosedProducts();
  }, []);

  const loadClosureRequests = async () => {
    try {
      const response = await axios.get('/products/closure-requests');
      setClosureRequests(response.data || []);
    } catch (error) {
      console.error('Kapatma talepleri yüklenemedi:', error);
    }
  };

  const loadClosedProducts = async () => {
    try {
      const response = await axios.get('/products/closed-products');
      setClosedProducts(response.data || []);
    } catch (error) {
      console.error('Kapanan ürünler yüklenemedi:', error);
    }
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      // HERKES HER ÜRÜNÜ GÖREBİLMELİ - FİLTRELEME YOK!
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('ERP load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Status filtresi (kart tıklamaları için)
    if (filters.statusFilter) {
      if (filters.statusFilter === 'nebim') {
        // Nebim kartı hem production_entry hem de nebim_entry statülerini gösterir
        filtered = filtered.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry');
      } else if (filters.statusFilter === 'publish_pending') {
        // TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.statusFilter === 'image_upload') {
        // Görsel filtresi: legacy olanlar hariç
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.statusFilter === 'published') {
        // "Yayında" = gerçekten canlıda olanlar (kapatılmış isActive=false olanlar hariç)
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.statusFilter);
      }
    }
    // statusFilter boşken ("Tüm Ürünler") tüm ürünler gösterilir

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAdminAction = async (productId: string) => {
    if (user?.role !== 'admin' && user?.role !== 'erp') {
      toast.error('Ticimax ID düzenleme yetkisi sadece admin veya ERP kullanıcılarına aittir!');
      return;
    }
    const product = products.find(p => p._id === productId);
    if (product) {
      setSelectedProduct(product);

      // Mevcut Ticimax ID'lerini yükle
      const existingIds = product.ticimaxProductIds || {};

      // Eğer eski tek ID varsa ve renkler varsa, ilk renge ata (geriye dönük uyumluluk)
      if (product.ticimaxProductId && !Object.keys(existingIds).length && product.productionInfo?.colors?.length) {
        existingIds[product.productionInfo.colors[0].code] = product.ticimaxProductId;
      }

      setErpData({
        ticimaxProductId: product.ticimaxProductId || 0,
        ticimaxProductIds: existingIds
      });

      setShowModal(true);
    }
  };

  // Direkt publish — /publish endpoint'i rol + status guard'larını kendisi yapıyor
  const handleDirectPublish = async (productId: string) => {
    try {
      await axios.post(`/products/${productId}/publish`, {
        notes: 'Ürün yayına alındı.'
      });

      toast.success('✅ Ürün başarıyla yayınlandı!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Yayınlama başarısız!');
      console.error('Direct publish error:', error);
    }
  };
  
  const handleErpTransfer = async () => {
    if (!selectedProduct) return;

    const colors = selectedProduct.productionInfo?.colors || [];
    const hasMultipleColors = colors.length > 0;
    
    // Validasyon
    if (hasMultipleColors) {
      // En az bir renk için Ticimax ID girilmiş olmalı
      const filledIds = Object.values(erpData.ticimaxProductIds).filter(id => id > 0);
      if (filledIds.length === 0) {
        toast.error('En az bir varyant için Ticimax Ürün ID giriniz!');
        return;
      }
    } else {
      // Tek renk veya renk tanımlı değilse eski yöntem
      if (!erpData.ticimaxProductId || erpData.ticimaxProductId <= 0) {
        toast.error('Ticimax Ürün ID giriniz!');
        return;
      }
    }
    
    try {
      // Ticimax ID'leri kaydet
      await axios.post(`/products/${selectedProduct._id}/erp-transfer`, {
        ticimaxProductId: hasMultipleColors 
          ? Object.values(erpData.ticimaxProductIds).find(id => id > 0) || 0 // İlk geçerli ID'yi eski alan için de kaydet
          : erpData.ticimaxProductId,
        ticimaxProductIds: hasMultipleColors ? erpData.ticimaxProductIds : undefined
      });

      toast.success('Ticimax ID\'ler kaydedildi!');
      setShowModal(false);
      setSelectedProduct(null);
      resetForms();
      loadProducts();
    } catch (error) {
      toast.error('Ticimax ID kaydedilemedi!');
      console.error('Ticimax ID save error:', error);
    }
  };

  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason
      });
      toast.success('Ürün geri yollandı!');
      loadProducts();
    } catch (error) {
      toast.error('Geri yollama başarısız!');
      console.error('Reject error:', error);
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
      loadClosureRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  const handleClosureResponse = async () => {
    if (!closureResponseModal.product || !closureResponseModal.decision) return;
    try {
      await axios.post(`/products/${closureResponseModal.product._id}/closure-respond`, {
        decision: closureResponseModal.decision,
        responseNote: responseNote.trim()
      });
      toast.success(closureResponseModal.decision === 'approved' ? 'Kapatma talebi onaylandı!' : 'Kapatma talebi reddedildi!');
      setClosureResponseModal({ show: false, product: null, decision: null });
      setResponseNote('');
      loadProducts();
      loadClosureRequests();
      loadClosedProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const handleReopenProduct = async () => {
    if (!reopenModal.product) return;
    try {
      await axios.post(`/products/${reopenModal.product._id}/reopen`, {
        note: reopenNote.trim()
      });
      toast.success('Ürün kartı başarıyla geri açıldı!');
      setReopenModal({ show: false, product: null });
      setReopenNote('');
      loadProducts();
      loadClosedProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const resetForms = () => {
    setErpData({
      ticimaxProductId: 0,
      ticimaxProductIds: {}
    });
  };

  const handleCardClick = (status: string) => {
    if (filters.statusFilter === status) {
      setFilters(prev => ({ ...prev, statusFilter: '' }));
    } else {
      setFilters(prev => ({ ...prev, statusFilter: status }));
    }
  };

  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    // Görsel aşamasındakiler: legacy (uploadedExternally=true) olanlar publish_pending'de sayılıyor
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    // TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  return (
    <AppLayout
      title="ERP Yönetim Paneli"
      subtitle="Ticimaxa Aktarım & Ürün Yayınlama İşlemleri"
    >
      
      {/* Ticimax ID Kayıt Modal */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Ticimax Ürün ID Kaydet</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedProduct.productionInfo?.productName}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowModal(false);
                  setSelectedProduct(null);
                  resetForms();
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Çoklu renk varyantları varsa her biri için ayrı input */}
              {selectedProduct.productionInfo?.colors && selectedProduct.productionInfo.colors.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>{selectedProduct.productionInfo.colors.length}</strong> renk varyantı bulundu. 
                      Her varyant için ayrı Ticimax Ürün ID girebilirsiniz.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {selectedProduct.productionInfo.colors.map((color) => (
                      <div key={color.code} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0 w-32">
                          <span className="text-sm font-medium text-gray-700">
                            {color.name}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">({color.code})</span>
                        </div>
                        <input
                          type="number"
                          value={erpData.ticimaxProductIds[color.code] || ''}
                          onChange={(e) => setErpData(prev => ({
                            ...prev,
                            ticimaxProductIds: {
                              ...prev.ticimaxProductIds,
                              [color.code]: parseInt(e.target.value) || 0
                            }
                          }))}
                          className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="Ticimax ID"
                          min="1"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Tek renk veya renk tanımlı değilse eski form
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Ticimax Ürün ID (UrunKartiID) *
                  </label>
                  <input
                    type="number"
                    value={erpData.ticimaxProductId || ''}
                    onChange={(e) => setErpData(prev => ({
                      ...prev,
                      ticimaxProductId: parseInt(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Örn: 13632"
                    min="1"
                    required
                  />
                </div>
              )}

              <div className="flex gap-3 pt-6 border-t">
                <Button
                  onClick={handleErpTransfer}
                  disabled={
                    selectedProduct.productionInfo?.colors?.length 
                      ? Object.values(erpData.ticimaxProductIds).filter(id => id > 0).length === 0
                      : !erpData.ticimaxProductId
                  }
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedProduct(null);
                    resetForms();
                  }}
                  className="px-8"
                >
                  İptal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* ERP Gösterge Paneli */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-900">ERP Görevleri</h3>
              <p className="text-sm text-blue-700 mt-1">
                Depo işlemi tamamlanan ürünleri ERP sistemine aktarın ve görsel yükleme işlemi tamamlanan ürünleri yayınlayın.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* 1. Ürün Bilgileri Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 2. Nebim İşlemleri (SKU + Barkod) */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'} (SKU/Barkod)</p>
            </CardContent>
          </Card>
          
          {/* 3. Üreticiye Gönderildi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 4. Stok Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary" className="bg-orange-600 text-white">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          {/* 5. Ticimaxa Aktarım */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          {/* 6. İçerik Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          {/* 7. Yayın Bekliyor */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary" className="bg-purple-600 text-white">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Yayın Onayı</p>
            </CardContent>
          </Card>
          
          {/* 9. Yayınlandı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Toplam Kart - Ayrı satırda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Tüm ürünler (filtreyi temizle)</p>
            </CardContent>
          </Card>

          {/* Kapanan Ürünler Kartı */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${showClosedProducts ? 'ring-2 ring-orange-500' : ''}`}
            onClick={() => setShowClosedProducts(!showClosedProducts)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Kapanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-orange-600 text-white">{closedProducts.length}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Kapatma talebi onaylanan ürünler</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'draft' && 'Ürün Girişi'}
              {filters.statusFilter === 'nebim' && 'Nebim İşlemleri'}
              {filters.statusFilter === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.statusFilter === 'stock_entry' && 'Stok Girişi'}
              {filters.statusFilter === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.statusFilter === 'content_entry' && 'İçerik'}
              {filters.statusFilter === 'image_upload' && 'Görsel'}
              {filters.statusFilter === 'publish_pending' && 'Yayın Bekliyor'}
              {filters.statusFilter === 'published' && 'Yayınlandı'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}
          
        {/* Kapatma Talepleri Bölümü */}
        {closureRequests.length > 0 && (
          <Card className="border-orange-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ban className="h-5 w-5 text-orange-600" />
                <span className="text-orange-900">Kapatma Talepleri</span>
                <Badge className="bg-orange-100 text-orange-800">{closureRequests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-orange-50">
                      <th className="text-left p-3 font-medium">Ürün Adı</th>
                      <th className="text-left p-3 font-medium">SKU</th>
                      <th className="text-left p-3 font-medium">Talep Eden</th>
                      <th className="text-left p-3 font-medium">Sebep</th>
                      <th className="text-left p-3 font-medium">Tarih</th>
                      <th className="text-center p-3 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closureRequests.map(product => {
                      const requester = product.closureRequest?.requestedBy;
                      const requesterName = typeof requester === 'object' && requester
                        ? (requester.name || requester.email || 'Bilinmeyen')
                        : 'Bilinmeyen';
                      const requestDate = product.closureRequest?.requestedAt
                        ? new Date(product.closureRequest.requestedAt).toLocaleString('tr-TR', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })
                        : '-';

                      return (
                        <tr key={product._id} className="border-b hover:bg-orange-50/30">
                          <td className="p-3">
                            <div className="font-medium">{product.productionInfo?.productName || 'İsimsiz Ürün'}</div>
                            <div className="text-xs text-muted-foreground">{product.productionInfo?.manufacturer}</div>
                          </td>
                          <td className="p-3 font-mono text-xs">{product.nebimInfo?.sku || '-'}</td>
                          <td className="p-3 text-sm">{requesterName}</td>
                          <td className="p-3">
                            <p className="text-sm max-w-xs truncate" title={product.closureRequest?.reason}>
                              {product.closureRequest?.reason || '-'}
                            </p>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{requestDate}</td>
                          <td className="p-3 text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => {
                                  setClosureResponseModal({ show: true, product, decision: 'approved' });
                                  setResponseNote('');
                                }}
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Onayla
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setClosureResponseModal({ show: true, product, decision: 'rejected' });
                                  setResponseNote('');
                                }}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Reddet
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Kapatma Talebi Yanıt Modalı */}
        {closureResponseModal.show && closureResponseModal.product && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    closureResponseModal.decision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {closureResponseModal.decision === 'approved'
                      ? <Check className="h-5 w-5 text-green-600" />
                      : <XCircle className="h-5 w-5 text-red-600" />
                    }
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {closureResponseModal.decision === 'approved' ? 'Kapatma Talebini Onayla' : 'Kapatma Talebini Reddet'}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {closureResponseModal.product.productionInfo?.productName}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  setClosureResponseModal({ show: false, product: null, decision: null });
                  setResponseNote('');
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Talep Nedeni</p>
                  <p className="text-sm font-medium">{closureResponseModal.product.closureRequest?.reason}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Yanıt Notu</label>
                  <textarea
                    value={responseNote}
                    onChange={(e) => setResponseNote(e.target.value)}
                    rows={3}
                    placeholder={closureResponseModal.decision === 'approved'
                      ? 'Onay ile ilgili notlarınız...'
                      : 'Red nedenini belirtiniz...'}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>

                {closureResponseModal.decision === 'approved' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        Onaylandığında ürün kartı pasife alınacaktır.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setClosureResponseModal({ show: false, product: null, decision: null });
                      setResponseNote('');
                    }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleClosureResponse}
                    className={`flex-1 ${
                      closureResponseModal.decision === 'approved'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {closureResponseModal.decision === 'approved' ? 'Onayla' : 'Reddet'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kapanan Ürünler Bölümü */}
        {showClosedProducts && (
          <Card className="border-gray-300">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Ban className="h-5 w-5 text-gray-600" />
                  <span>Kapanan Ürünler</span>
                  <Badge className="bg-gray-100 text-gray-800">{closedProducts.length}</Badge>
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowClosedProducts(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {closedProducts.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">Henüz kapanan ürün yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium">Ürün Adı</th>
                        <th className="text-left p-3 font-medium">SKU</th>
                        <th className="text-left p-3 font-medium">Kapatma Nedeni</th>
                        <th className="text-left p-3 font-medium">Talep Eden</th>
                        <th className="text-left p-3 font-medium">Onaylayan</th>
                        <th className="text-left p-3 font-medium">Talep Tarihi</th>
                        <th className="text-left p-3 font-medium">Onay Tarihi</th>
                        <th className="text-center p-3 font-medium">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closedProducts.map(product => {
                        const requester = product.closureRequest?.requestedBy;
                        const requesterName = typeof requester === 'object' && requester
                          ? (requester.name || requester.email || '-')
                          : '-';
                        const responder = product.closureRequest?.respondedBy;
                        const responderName = typeof responder === 'object' && responder
                          ? (responder.name || responder.email || '-')
                          : '-';
                        const requestDate = product.closureRequest?.requestedAt
                          ? new Date(product.closureRequest.requestedAt).toLocaleString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })
                          : '-';
                        const respondDate = product.closureRequest?.respondedAt
                          ? new Date(product.closureRequest.respondedAt).toLocaleString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })
                          : '-';

                        return (
                          <tr key={product._id} className="border-b hover:bg-gray-50/50">
                            <td className="p-3">
                              <div className="font-medium">{product.productionInfo?.productName || 'İsimsiz Ürün'}</div>
                              <div className="text-xs text-muted-foreground">{product.productionInfo?.manufacturer}</div>
                            </td>
                            <td className="p-3 font-mono text-xs">{product.nebimInfo?.sku || '-'}</td>
                            <td className="p-3">
                              <p className="text-sm max-w-xs" title={product.closureRequest?.reason}>
                                {product.closureRequest?.reason || '-'}
                              </p>
                              {product.closureRequest?.responseNote && (
                                <p className="text-xs text-green-700 mt-1">
                                  <span className="font-medium">Onay notu:</span> {product.closureRequest.responseNote}
                                </p>
                              )}
                            </td>
                            <td className="p-3 text-sm">{requesterName}</td>
                            <td className="p-3 text-sm">{responderName}</td>
                            <td className="p-3 text-xs text-muted-foreground">{requestDate}</td>
                            <td className="p-3 text-xs text-muted-foreground">{respondDate}</td>
                            <td className="p-3 text-center">
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => {
                                  setReopenModal({ show: true, product });
                                  setReopenNote('');
                                }}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Geri Aç
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Geri Aç Modalı */}
        {reopenModal.show && reopenModal.product && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Ürün Kartını Geri Aç</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {reopenModal.product.productionInfo?.productName}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  setReopenModal({ show: false, product: null });
                  setReopenNote('');
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Kapatma Nedeni</p>
                  <p className="text-sm">{reopenModal.product.closureRequest?.reason || '-'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Geri Açma Notu</label>
                  <textarea
                    value={reopenNote}
                    onChange={(e) => setReopenNote(e.target.value)}
                    rows={3}
                    placeholder="Geri açma nedenini belirtiniz (opsiyonel)..."
                    className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReopenModal({ show: false, product: null });
                      setReopenNote('');
                    }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleReopenProduct}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Geri Aç
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Ürün bulunamadı</p>
              </div>
            ) : (
              <ProductTable
                products={filteredProducts}
                onRefresh={loadProducts}
                onErpTransfer={(productId) => handleAdminAction(productId)}
                onPublish={handleDirectPublish}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
                showAllActions={false}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ErpDashboard;
